import React, { useMemo } from 'react';
import { type BypassTableRow, bypassColumnDef } from './BypassTableDefinition';
import { DataTable } from 'src/portal/shared-compat/DataTable/DataTable';
import { useClientDataTable } from '../../shared-compat/DataTable/hooks';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getLegacyPortalApi } from '../../legacy-portal-api/legacy-api';
import { type MenuConfig } from 'src/portal/menu-modal/MenuModal/MenuModal';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { useModal } from 'src/portal/hooks/useModal';
import { reportException } from '../../../util/sentryProvider';
import { isEmptyOrWhiteSpace } from '../../../common/util';

export function useBypassTable() {
  const api = getLegacyPortalApi();
  const bypassColumns = useMemo(() => bypassColumnDef(), []);

  const url = new URL(window.location.href);

  const siteUuid = url.searchParams.get('s');
  const assetUuid = url.searchParams.get('a');
  const companyUuid = url.searchParams.get('c');

  const getActiveBypassesQuery = useQuery({
      queryKey: ['bypasses'],
      queryFn: async () => {
        switch (true) {
          case !!assetUuid && !!siteUuid:
            return api.getActiveBypassesByAsset(siteUuid, assetUuid);
          case !!siteUuid:
            return api.getActiveBypassesByInstallation(siteUuid);
          case !!companyUuid:
            return api.getActiveBypassesByCompany(companyUuid);
          default:
            return api.getActiveBypassesAllCompanies();
        }
      },
      gcTime: 0
    }),
    { refetch } = getActiveBypassesQuery;

  const bypassTable = useClientDataTable({
    initialPageSize: 100,
    enableGlobalSearchBar: true,
    isFetching: getActiveBypassesQuery.isLoading,
    data: getActiveBypassesQuery.data ?? [],
    // @ts-ignore - suppressing this error because this will be converted to server table once the backend is implemented.
    columns: bypassColumns
  });

  return { bypassTable, refetch };
}

export function BypassTable() {
  const { bypassTable } = useBypassTable();

  const api = getLegacyPortalApi();

  const clearBypass = useMutation({
    mutationFn: async (row: BypassTableRow) => {
      const isSiteBypass = row.RmxSiteId !== '-1' && (row.RmxAssetId === '-1' || isEmptyOrWhiteSpace(row.RmxAssetId)) && isEmptyOrWhiteSpace(row.RmxBoxId);
      if (isSiteBypass) {
        await api.clearSiteBypass(row.SiteUuid);
      } else if (row.RmxBoxId) {
        // this does not get set to -1
        await api.clearRmxBoxBypass(row.RmxBoxId, row.RmxSiteId);
      } else if (row.SystemProcessId !== '-1') {
        // this does get set to -1 if it does not exist
        await api.clearSystemProcessBypass(row.SystemProcessId!, row.RmxSiteId);
      } else {
        await api.clearAssetBypassByType(row.RmxAssetId, 'AutomaticClear');
      }

      // reloading for now as a quick fix so that the alert page updates as well on bypass clear
      // TODO - refetch both the alert page and the bypass page instead of reloading
      window.location.reload();
      confirmationDialog.close();
    },
    onError: (error) => {
      reportException(error);
    }
  });

  const confirmationDialog = useModal<BypassTableRow>();
  const getMenuConfig = (row: BypassTableRow) => {
    return {
      getLabel: () => 'Bypass',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      bypass_clear: async () => {
        await confirmationDialog.open(row);
      },
      portalContactModal: async () => {
        await api.OpenContactList(row.SiteUuid);
      }
    } satisfies MenuConfig<BypassTableRow>;
  };

  return (
    <div style={{ padding: '10px' }}>
      {/*<h3 className="m-t-10"><i className="far fa-lg fa-fw m-r-10 fa-bell-slash" /> BYPASSES</h3>*/}
      <h3 className='m-t-10 mb-3'>
        <i className='fas fa-lg fa-fw m-r-10 fa-bell-slash' /> BYPASSES
      </h3>
      <DataTable onRowClick={(row) => window.OpenMenuModal('Bypass', getMenuConfig(row))} enableExport={true} table={bypassTable} />
      <ConfirmationDialog
        title={`Clear Bypass`}
        open={confirmationDialog.isOpen}
        onAccept={async () => {
          const row = confirmationDialog!.param!;
          if (row.BypassType === 'ActivitySlam') {
            alert(
              'This bypass is a activity slam bypass, a tech is currently working on the asset and the bypass will be cleared automatically once the activity is completed.'
            );
          } else {
            await clearBypass.mutateAsync(row);
          }
        }}
        onDecline={() => confirmationDialog.close()}
        prompt='Are you sure you want to clear this bypass?'
        positiveText='Yes'
        negativeText='No'
      />
    </div>
  );
}
